import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay'
import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core'
import { defaultDate } from '@localization-lib/date-time/dayjs/dayjsHelper'
import { Store } from '@ngrx/store'
import { Notification } from '@platform-lib/notification/notification.model'
import {
    loadEntries,
    setIsRead,
} from '@platform-lib/notification/redux/notification.actions'
import {
    isLoadingEntries,
    selectOverlayEntries,
    selectOverlayEntryLoadingError,
    selectOverlayNotificationCount,
} from '@platform-lib/notification/redux/notification.selectors'
import { Animations } from '@shared-util-lib/animations/animations'
import { TrackingCategory } from '@tracking-lib/tracking.model'
import {
    NotificationAction,
    NotificationArea,
} from '@tracking-lib/use-case-tracking-models/notification.tracking.model'
import { Subscription } from 'rxjs'
import { map, shareReplay, tap, withLatestFrom } from 'rxjs/operators'

import dayjs from 'dayjs'

export interface NotificationChunk {
    label: string
    notifications: Notification[]
    date: dayjs.Dayjs
    showDate?: boolean
}

@Component({
    selector: 'app-notification-panel',
    templateUrl: './notification-panel.component.html',
    styleUrls: ['./notification-panel.component.scss'],
    animations: [Animations.overlay],
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
    @ViewChild('overlayHeader') overlayHeaderRef?: ElementRef<HTMLElement>

    overlayIsOpen = false

    unreadNotificationIds: string[] = []

    readonly overlayPositions: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
        },
    ]

    readonly overlayOrigin = new CdkOverlayOrigin(this.elementRef)

    readonly error$ = this.store.select(selectOverlayEntryLoadingError)

    readonly notifications$ = this.store.select(selectOverlayEntries).pipe(
        map((notifications) => notifications.slice(0, 15)),
        tap(
            (notifications) =>
                (this.unreadNotificationIds = notifications
                    .filter((notification) => notification.isRead === false)
                    .map((notification) => notification.id))
        ),
        shareReplay(1)
    )

    readonly allRead$ = this.notifications$.pipe(
        map(
            (notifications) =>
                notifications.filter((notification) => !notification.isRead)
                    .length === 0
        )
    )

    readonly chunks$ = this.notifications$.pipe(
        map((notifications) => this.createChunks(notifications))
    )

    private createChunks(notifications: Notification[]): NotificationChunk[] {
        const today = defaultDate().startOf('day')
        const yesterday = today.subtract(1, 'days')
        const startOfWeek = defaultDate().startOf('week')
        const startOfLastWeek = startOfWeek.subtract(1, 'week')

        const chunks = [
            this.chunkOf('Today', [], today),
            this.chunkOf('Yesterday', [], yesterday, true),
            this.chunkOf('This week', [], startOfWeek),
            this.chunkOf('Last week', [], startOfLastWeek),
            this.chunkOf('Previous', [], defaultDate('2000-01-01')),
        ]

        notifications.forEach((notification) => {
            const notificationDate = defaultDate(notification.occurredAt)
            chunks
                .find((chunk) => chunk.date.isBefore(notificationDate))
                ?.notifications.push(notification)
        })

        return chunks.filter((chunk) => chunk.notifications.length > 0)
    }

    private chunkOf(
        label: string,
        notifications: Notification[],
        date: dayjs.Dayjs,
        showDate?: boolean
    ): NotificationChunk {
        return {
            label: label,
            notifications: notifications,
            date: date,
            showDate: showDate ?? false,
        }
    }

    readonly notificationsLoading$ = this.store
        .select(isLoadingEntries)
        .pipe(shareReplay(1))

    // show pixel loader only if dropdown wasn't empty before loading
    readonly loading$ = this.notificationsLoading$.pipe(
        withLatestFrom(this.store.select(selectOverlayNotificationCount)),
        map(([loading, count]) => loading && 0 < count),
        shareReplay(1)
    )

    // show skeletons only if dropdown was empty before loading
    readonly showSkeletons$ = this.notificationsLoading$.pipe(
        withLatestFrom(this.store.select(selectOverlayNotificationCount)),
        map(([loading, count]) => loading && !count),
        shareReplay(1)
    )

    private subscription = new Subscription()

    constructor(
        public elementRef: ElementRef,
        private renderer: Renderer2,
        private store: Store
    ) {}

    ngOnInit() {
        this.renderer.listen(window, 'click', this.onWindowClick.bind(this))
    }

    ngOnDestroy() {
        this.subscription.unsubscribe()
    }

    toggleOverlay() {
        this.overlayIsOpen = !this.overlayIsOpen
        if (this.overlayIsOpen) {
            this.store.dispatch(loadEntries({}))
        }
    }

    onWindowClick(event: MouseEvent) {
        const path = event.composedPath()
        const element = this.elementRef.nativeElement
        const overlay = this.overlayHeaderRef?.nativeElement
        // close overlay if it's open only when the panel and bell aren't in the event path
        if (
            this.overlayIsOpen &&
            path.indexOf(element) === -1 &&
            (!overlay || path.indexOf(overlay) === -1)
        ) {
            this.toggleOverlay()
        }
    }

    protected readonly TrackingCategory = TrackingCategory
    protected readonly NotificationArea = NotificationArea
    protected readonly NotificationAction = NotificationAction

    markAllAsRead(event) {
        event.preventDefault()
        event.stopPropagation()

        if (this.unreadNotificationIds.length === 0) {
            return
        }

        this.store.dispatch(
            setIsRead({
                request: {
                    notificationIds: this.unreadNotificationIds,
                },
            })
        )
    }
}
